import type { QueryClient } from "@tanstack/react-query";
import type { AuthChangeEvent, Session } from "@supabase/supabase-js";

/**
 * Cached page data belongs to the account that loaded it. All portals share
 * one browser cache, so when the signed-in identity changes (sign-out, or a
 * different account signing in) every cached row from the previous account
 * is dropped before anything can render it to the next one.
 *
 * Synchronous on purpose: it runs inside onAuthStateChange, which must never
 * await (auth-js#762).
 */
export function createSessionCacheBoundary(queryClient: QueryClient) {
  let knownUserId: string | null | undefined;

  return (event: AuthChangeEvent, session: Session | null) => {
    const nextUserId = session?.user?.id ?? null;
    // A failed refresh reports no session: treat it as signed out.
    const signedOut = event === "SIGNED_OUT" || (event === "TOKEN_REFRESHED" && !session);
    const effectiveId = signedOut ? null : nextUserId;

    if (knownUserId === undefined) {
      knownUserId = effectiveId;
      return;
    }
    if (effectiveId === knownUserId) return;

    knownUserId = effectiveId;
    if (effectiveId === null) {
      // Nothing to refetch for a signed-out visitor.
      queryClient.clear();
      return;
    }
    // A different account: drop everything, and reload what is on screen as the new account.
    queryClient.removeQueries({ type: "inactive" });
    void queryClient.resetQueries({ type: "active" });
    queryClient.getMutationCache().clear();
  };
}
